import { DEGREE_TO_FUNCTIONS, getDefaultFunctionForChord, buildChordContext } from './riemannFunctions'
import { WORK_TYPE_REGEX } from './workTypeKeywords'
import { composerToSlug } from '../services/worksReference'
import { getMusicalEra, formatEraTag } from '../data/musicalEras'
import { inferBorrowedFromDegree } from './chordInference'

/**
 * Mapping des compositeurs vers leurs périodes stylistiques
 * Structure extensible pour ajouter facilement de nouveaux compositeurs
 */
const COMPOSER_STYLE_MAP = {
  // Baroque (1600-1750)
  'Bach': 'Baroque',
  'J.S. Bach': 'Baroque',
  'Johann Sebastian Bach': 'Baroque',
  'Vivaldi': 'Baroque',
  'Handel': 'Baroque',
  'Händel': 'Baroque',
  'Telemann': 'Baroque',
  'Purcell': 'Baroque',
  'Scarlatti': 'Baroque',
  'D. Scarlatti': 'Baroque',
  'Corelli': 'Baroque',
  'Couperin': 'Baroque',
  
  // Classique (1750-1820)
  'Mozart': 'Classique',
  'W.A. Mozart': 'Classique',
  'Wolfgang Amadeus Mozart': 'Classique',
  'Haydn': 'Classique',
  'F.J. Haydn': 'Classique',
  'Franz Joseph Haydn': 'Classique',
  'Beethoven': 'Classique', // Transition Classique/Romantique
  'L. van Beethoven': 'Classique',
  'Ludwig van Beethoven': 'Classique',
  'Clementi': 'Classique',
  'Gluck': 'Classique',
  'Salieri': 'Classique',
  
  // Romantique (1820-1900)
  'Chopin': 'Romantique',
  'F. Chopin': 'Romantique',
  'Frédéric Chopin': 'Romantique',
  'Schumann': 'Romantique',
  'R. Schumann': 'Romantique',
  'Robert Schumann': 'Romantique',
  'Liszt': 'Romantique',
  'F. Liszt': 'Romantique',
  'Franz Liszt': 'Romantique',
  'Brahms': 'Romantique',
  'J. Brahms': 'Romantique',
  'Johannes Brahms': 'Romantique',
  'Wagner': 'Romantique',
  'R. Wagner': 'Romantique',
  'Richard Wagner': 'Romantique',
  'Tchaikovsky': 'Romantique',
  'Tchaïkovski': 'Romantique',
  'P.I. Tchaikovsky': 'Romantique',
  'Dvořák': 'Romantique',
  'A. Dvořák': 'Romantique',
  'Antonín Dvořák': 'Romantique',
  'Mendelssohn': 'Romantique',
  'F. Mendelssohn': 'Romantique',
  'Felix Mendelssohn': 'Romantique',
  'Schubert': 'Romantique',
  'F. Schubert': 'Romantique',
  'Franz Schubert': 'Romantique',
  'Verdi': 'Romantique',
  'G. Verdi': 'Romantique',
  'Giuseppe Verdi': 'Romantique',
  'Richard Strauss': 'Romantique',
  'R. Strauss': 'Romantique',
  'Strauss': 'Romantique',

  // Moderne (1900+)
  'Debussy': 'Moderne',
  'C. Debussy': 'Moderne',
  'Claude Debussy': 'Moderne',
  'Ravel': 'Moderne',
  'M. Ravel': 'Moderne',
  'Maurice Ravel': 'Moderne',
  'Stravinsky': 'Moderne',
  'I. Stravinsky': 'Moderne',
  'Igor Stravinsky': 'Moderne',
  'Schoenberg': 'Moderne',
  'A. Schoenberg': 'Moderne',
  'Arnold Schoenberg': 'Moderne',
  'Bartók': 'Moderne',
  'B. Bartók': 'Moderne',
  'Béla Bartók': 'Moderne',
  'Prokofiev': 'Moderne',
  'S. Prokofiev': 'Moderne',
  'Shostakovich': 'Moderne',
  'D. Shostakovich': 'Moderne',
  'Dmitri Shostakovich': 'Moderne'
}

/**
 * Détermine le style musical d'un compositeur
 * @param {string} composer - Le nom du compositeur
 * @returns {string|null} - Le style (Baroque, Classique, Romantique, Moderne) ou null si inconnu
 */
export function getComposerStyle(composer) {
  if (!composer) return null

  const normalizedComposer = composer.trim()

  // Recherche exacte d'abord
  if (COMPOSER_STYLE_MAP[normalizedComposer]) {
    return COMPOSER_STYLE_MAP[normalizedComposer]
  }

  // Recherche partielle (pour gérer les variations de nommage)
  for (const [key, style] of Object.entries(COMPOSER_STYLE_MAP)) {
    if (normalizedComposer.toLowerCase().includes(key.toLowerCase()) ||
        key.toLowerCase().includes(normalizedComposer.toLowerCase())) {
      return style
    }
  }

  // TODO: Intégration future avec une API externe pour les compositeurs non mappés
  // Pour l'instant, retourner null si non trouvé
  return null
}

/**
 * Normalise un degré pour la comparaison (enlève les altérations)
 * @param {string} degree - Le degré (peut contenir des altérations)
 * @returns {string} - Le degré normalisé (I, II, III, etc.)
 */
function normalizeDegree(degree) {
  if (!degree) return null
  // Enlever les altérations et normaliser en majuscules
  return degree.toString().replace(/[♭#b]/g, '').toUpperCase()
}

/**
 * Normalise un mode en famille pour l'analyse des emprunts.
 * @param {string|null|undefined} degreeMode
 * @returns {'major'|'minor'|'other'|null}
 */
function normalizeModeFamily(degreeMode) {
  if (!degreeMode || typeof degreeMode !== 'string') return null
  const mode = degreeMode.trim().toLowerCase()
  if (!mode || mode === 'generic') return null
  if (mode === 'major' || mode === 'ionian') return 'major'
  if (mode === 'minor' || mode === 'aeolian') return 'minor'
  return 'other'
}

/**
 * Détermine le mode de référence d'un exercice (major/minor) à partir des accords.
 * Priorité aux accords non empruntés pour éviter de biaiser le contexte tonal.
 * @param {Array<object|null>} chords
 * @returns {'major'|'minor'|null}
 */
function getReferenceModeForBorrowedTags(chords) {
  if (!Array.isArray(chords) || chords.length === 0) return null

  const countModes = (list) => {
    const counts = { major: 0, minor: 0 }
    list.forEach((chord) => {
      const family = normalizeModeFamily(chord?.degreeMode)
      if (family === 'major') counts.major++
      else if (family === 'minor') counts.minor++
    })
    return counts
  }

  const nonBorrowed = chords.filter((chord) => chord && chord.isBorrowed !== true)
  let counts = countModes(nonBorrowed)

  // Fallback : s'il n'y a pas assez d'info hors emprunts, utiliser tous les accords.
  if (counts.major === 0 && counts.minor === 0) {
    counts = countModes(chords)
  }

  if (counts.major === 0 && counts.minor === 0) return null
  if (counts.major === counts.minor) return null
  return counts.major > counts.minor ? 'major' : 'minor'
}

/**
 * Détecte les progressions harmoniques dans une séquence d'accords
 * @param {Array} chordData - Tableau des données d'accords
 * @returns {Array} - Tableau des tags de progression détectés
 */
function detectProgressions(chordData) {
  const progressions = []
  if (!chordData || chordData.length < 2) return progressions
  
  // Extraire les degrés normalisés
  const degrees = chordData.map(chord => {
    if (!chord) return null
    return normalizeDegree(chord.degree || chord.root)
  }).filter(d => d !== null)
  
  if (degrees.length < 2) return progressions
  
  // Détecter II-V-I (ou variations)
  for (let i = 0; i < degrees.length - 2; i++) {
    const seq = [degrees[i], degrees[i + 1], degrees[i + 2]].join('-')
    if (seq === 'II-V-I' || seq === 'II-VI-I' || seq === 'II-V-VI') {
      progressions.push('ProgressionII-V-I')
      break // Ne détecter qu'une fois
    }
  }
  
  // Détecter Circle of Fifths (progression par quintes descendantes)
  // Chaque degré résout une quinte en dessous : IV→VII→III→VI→II→V→I
  const FIFTHS_NEXT = { I: 'IV', IV: 'VII', VII: 'III', III: 'VI', VI: 'II', II: 'V', V: 'I' }
  let consecutiveFifths = 0
  for (let i = 0; i < degrees.length - 1; i++) {
    if (FIFTHS_NEXT[degrees[i]] === degrees[i + 1]) {
      consecutiveFifths++
    } else {
      consecutiveFifths = 0
    }
    if (consecutiveFifths >= 3) {
      progressions.push('ProgressionCircleOfFifths')
      break
    }
  }
  
  return [...new Set(progressions)]
}

/**
 * Génère des tags automatiques à partir des marqueurs d'accords
 * @param {Array} markers - Tableau des marqueurs temporels
 * @param {Object} chordData - Objet contenant les données d'accords par index
 * @param {string|null} composer - Nom du compositeur (optionnel, pour tags stylistiques)
 * @returns {Array} - Tableau des tags générés
 */
export function generateAutoTags(markers, chordData, composer = null) {
  const tags = []
  const roots = new Set()
  const functions = new Set()
  const modes = { major: 0, minor: 0 }

  const chordArray = markers.map((_, i) => chordData[i])
  const resolvedChords = inferBorrowedFromDegree(chordArray)
  const referenceMode = getReferenceModeForBorrowedTags(resolvedChords)

  // Analyser chaque marqueur
  markers.forEach((marker, index) => {
    const chord = resolvedChords[index]
    if (!chord) return

    // Racine et degré
    if (chord.root) {
      roots.add(chord.root)
    }
    
    
    // Détection des fonctions harmoniques
    if (chord.selectedFunction) {
      if (chord.selectedFunction === 'T') {
        functions.add('T')
        tags.push('Tonique')
      } else if (chord.selectedFunction === 'D') {
        functions.add('D')
        tags.push('Dominante')
      } else if (chord.selectedFunction === 'SD') {
        functions.add('SD')
        tags.push('SousDominante')
      }
    } else if (chord.degree) {
      const degreeNormalized = normalizeDegree(chord.degree)
      const context = buildChordContext(resolvedChords, index)
      const inferredFunction = getDefaultFunctionForChord(chord, context)
      if (inferredFunction) {
        functions.add(inferredFunction)
        if (inferredFunction === 'T' && !tags.includes('Tonique')) {
          tags.push('Tonique')
        } else if (inferredFunction === 'D' && !tags.includes('Dominante')) {
          tags.push('Dominante')
        } else if (inferredFunction === 'SD' && !tags.includes('SousDominante')) {
          tags.push('SousDominante')
        }
      } else {
        const chordFunctions = DEGREE_TO_FUNCTIONS[degreeNormalized] || []
        chordFunctions.forEach(func => {
          functions.add(func)
          if (func === 'T' && !tags.includes('Tonique')) {
            tags.push('Tonique')
          } else if (func === 'D' && !tags.includes('Dominante')) {
            tags.push('Dominante')
          } else if (func === 'SD' && !tags.includes('SousDominante')) {
            tags.push('SousDominante')
          }
        })
      }
    }
    
    // Détection des emprunts modaux
    if (chord.isBorrowed) {
      tags.push('EmpruntModal')
      const borrowedModeFamily = normalizeModeFamily(chord.degreeMode)
      if (borrowedModeFamily === 'other') {
        tags.push('EmpruntAutreMode')
      } else if (
        referenceMode &&
        (borrowedModeFamily === 'major' || borrowedModeFamily === 'minor') &&
        borrowedModeFamily !== referenceMode
      ) {
        tags.push('EmpruntTonHomonyme')
      }
    }
    
    // Détection du contexte modal (ionian = major, aeolian = minor pour les tags Majeur/Mineur)
    if (chord.degreeMode) {
      if (chord.degreeMode === 'major' || chord.degreeMode === 'ionian') {
        modes.major++
      } else if (chord.degreeMode === 'minor' || chord.degreeMode === 'aeolian') {
        modes.minor++
      }
    }

    // Accords spéciaux
    if (chord.specialRoot) {
      if (chord.specialRoot === 'Gr') {
        tags.push('SixteAugmentée')
        tags.push('Allemande')
      } else if (chord.specialRoot === 'Fr') {
        tags.push('SixteAugmentée')
        tags.push('Française')
      } else if (chord.specialRoot === 'It') {
        tags.push('SixteAugmentée')
        tags.push('Italienne')
      } else if (chord.specialRoot === 'N' || chord.specialRoot === 'IIb') {
        tags.push('Napolitaine')
      }
    }

    // Cadence 6/4 (I 6/4 cadentiel, résout sur V)
    if (chord.degree && (chord.figure === '64' || chord.inversion === '64') && chord.sixFourVariant === 'cadential') {
      if (!tags.includes('Cadence6/4')) tags.push('Cadence6/4')
    }

    // Dominantes secondaires (V/V, V/IV, V/VI, etc.) — borrowedFromDegree ou ofDegree sur l'accord
    const hasSecondaryDominant = (chord.borrowedFromDegree != null && chord.borrowedFromDegree !== '') || (chord.ofDegree != null && chord.ofDegree !== '')
    if (hasSecondaryDominant && !tags.includes('DominanteSecondaire')) {
      tags.push('DominanteSecondaire')
    }

    // Qualités
    if (chord.quality) {
      if (chord.quality === '°') {
        tags.push('Diminué')
      } else if (chord.quality === '+') {
        tags.push('Augmenté')
      }
    }

    // Renversements
    if (chord.inversion) {
      if (chord.inversion === '6') {
        tags.push('PremierRenversement')
      } else if (chord.inversion === '64') {
        tags.push('SecondRenversement')
      } else if (chord.inversion === '65') {
        tags.push('SeptièmePremierRenversement')
      } else if (chord.inversion === '43') {
        tags.push('SeptièmeSecondRenversement')
      } else if (chord.inversion === '2' || chord.inversion === '42') {
        tags.push('SeptièmeTroisièmeRenversement')
      }
    }

    // Extensions
    if (chord.extension) {
      if (chord.extension === '7') {
        tags.push('Septième')
      } else if (chord.extension === '9') {
        tags.push('Neuvième')
      } else if (chord.extension === '11') {
        tags.push('Onzième')
      } else if (chord.extension === '13') {
        tags.push('Treizième')
      }
    }

    // Retards
    if (chord.delay) {
      tags.push('Retard')
      if (chord.delay === '54') {
        tags.push('RetardTierce')
      }
    }

    // Cadences
    if (chord.cadence) {
      if (chord.cadence === 'perfect') {
        tags.push('CadenceParfaite')
      } else if (chord.cadence === 'imperfect') {
        tags.push('CadenceImparfaite')
      } else if (chord.cadence === 'plagal') {
        tags.push('CadencePlagale')
      } else if (chord.cadence === 'deceptive') {
        tags.push('CadenceRompue')
      } else if (chord.cadence === 'half' || chord.cadence === 'demi-cadence') {
        tags.push('DemiCadence')
      }
    }
  })

  // Tierce Picarde : dernier accord = I majeur dans une tonalité mineure
  if (markers.length > 0) {
    const lastChord = resolvedChords[markers.length - 1]
    if (lastChord && lastChord.degree === 'I' && lastChord.figure === '5') {
      const hasMinorContext = modes.minor > modes.major || tags.includes('Mineur')
      if (hasMinorContext && !lastChord.isBorrowed) {
        tags.push('TiercePicarde')
      }
    }
  }


  // Ajouter les degrés utilisés
  roots.forEach(root => {
    if (root.match(/^[IVX]+$/)) {
      tags.push(`Degré${root}`)
    }
  })
  
  // Détecter les progressions harmoniques
  const chordArrayForProgressions = resolvedChords.filter(c => c !== null && c !== undefined)
  if (chordArrayForProgressions.length >= 2) {
    const detectedProgressions = detectProgressions(chordArrayForProgressions)
    tags.push(...detectedProgressions)
  }
  
  // Détecter le contexte modal
  const totalModes = modes.major + modes.minor
  if (totalModes > 0) {
    const majorRatio = modes.major / totalModes
    const minorRatio = modes.minor / totalModes
    // Si > 60% en majeur ou mineur, ajouter le tag correspondant
    if (majorRatio > 0.6) {
      tags.push('Majeur')
    } else if (minorRatio > 0.6) {
      tags.push('Mineur')
    }
  }
  
  // Tags stylistiques basés sur le compositeur
  if (composer) {
    const style = getComposerStyle(composer)
    if (style) {
      tags.push(style)
    }
  }

  // Retirer les doublons et retourner
  const finalTags = [...new Set(tags)]
  return finalTags
}

/** Styles Nouveaux Horizons (non musique classique) — id technique → tag affiché */
export const HORIZONS_MUSIC_CATEGORIES = {
  film: { id: 'film', label: 'Musique de film', tagLabel: 'Film' },
  game: { id: 'game', label: 'Jeu vidéo', tagLabel: 'JeuVideo' },
  anime: { id: 'anime', label: 'Anime', tagLabel: 'Anime' },
  variety: { id: 'variety', label: 'Variété', tagLabel: 'Variété' },
  pop: { id: 'pop', label: 'Pop', tagLabel: 'Pop' }
}

/** Ordre des styles pour l’affichage (déblocage progressif) */
export const HORIZONS_STYLE_ORDER = ['film', 'game', 'anime', 'variety', 'pop']

/**
 * Retourne les tags à ajouter pour un exercice Nouveaux Horizons (section + style).
 * @param {string} musicCategory - 'film' | 'game' | 'anime' | 'variety' | 'pop'
 * @returns {string[]} - ex. ['Horizons', 'Film']
 */
/**
 * Retourne un tag de décennie à partir d'une année (ex. 2001 → '2000s', 1994 → '1990s').
 * @param {number|string|null} year
 * @returns {string|null}
 */
export function getDecadeTag(year) {
  if (year == null) return null
  const y = Number(year)
  if (isNaN(y) || y < 1900) return null
  return `${Math.floor(y / 10) * 10}s`
}

/**
 * Retourne le tag d'ère musicale pour un exercice Horizons.
 * @param {string} musicCategory - 'film' | 'game' | 'anime' | 'variety' | 'pop'
 * @param {number|string|null} year
 * @returns {string|null} - ex. 'EreSynthpopNewWave', 'EreChiptune'
 */
export function getEraTagForExercise(musicCategory, year) {
  if (!musicCategory || year == null) return null
  const era = getMusicalEra(musicCategory, year)
  if (!era) return null
  return formatEraTag(era.tagLabel)
}

export function getHorizonsTagsForCategory(musicCategory) {
  if (!musicCategory) return []
  const cat = HORIZONS_MUSIC_CATEGORIES[musicCategory]
  if (!cat) return ['Horizons']
  return ['Horizons', cat.tagLabel]
}

/** Tempos courants pour valider "I. Allegro" / "II. Andante" */
const TEMPO_WORDS = new Set(['allegro', 'andante', 'adagio', 'largo', 'presto', 'scherzo', 'menuet', 'menuetto', 'rondo', 'molto', 'allegretto', 'andantino', 'larghetto', 'largamente', 'vivace', 'grave', 'lento', 'moderato', 'adagietto', 'finale', 'introduction', 'introduzione'])

function looksLikeMovement(s) {
  if (!s || s.length > 50) return false
  const t = s.trim()
  // Chiffre romain + optionnel point + optionnel tempo : "II. Adagio", "III", "IV. Scherzo"
  const romanTempo = /^\s*[IVXLCDM]+\s*\.?\s*[A-Za-zÀ-ÿ]*\s*$/i
  if (romanTempo.test(t)) {
    const rest = t.replace(/^\s*[IVXLCDM]+\s*\.?\s*/i, '').trim()
    if (!rest || TEMPO_WORDS.has(rest.toLowerCase())) return true
    return true
  }
  // "2nd movement", "Movement 2", "Mov. 2", "2e mouvement", "1er mvt"
  if (/(?:\d(?:st|nd|rd|th)?\s+)?(?:movement|mov\.?|mouvement|mvt)\s*\.?\s*\d?$/i.test(t)) return true
  if (/^(?:movement|mov\.?|mouvement|mvt)\s+\d\s*$/i.test(t)) return true
  return false
}

/**
 * Extrait un éventuel segment "mouvement" à la fin du titre (après dernier " - " ou " – ")
 */
function extractMovementFromWorkTitle(workTitle) {
  if (!workTitle) return { workTitle: workTitle || '', movementTitle: null }
  const sep = workTitle.match(/\s+[-–—]\s+([^-–—]+)$/)
  if (!sep) return { workTitle: workTitle.trim(), movementTitle: null }
  const candidate = sep[1].trim()
  if (!looksLikeMovement(candidate)) return { workTitle: workTitle.trim(), movementTitle: null }
  const main = workTitle.slice(0, sep.index).trim()
  return { workTitle: main || workTitle.trim(), movementTitle: candidate }
}

/**
 * Retire les suffixes courants en fin de titre (Live, HD, Official Video, année, etc.).
 */
function stripCommonSuffixes(t) {
  if (!t || typeof t !== 'string') return t
  let s = t.trim()
  const suffixPatterns = [
    /\s*[|\-–—]\s*(?:official\s+video|video\s+officiel|hd|4k|live)\s*$/i,
    /\s*\(\s*(?:live|hd|4k|official\s+video)\s*\)\s*$/i,
    /\s*-\s*(?:19|20)\d{2}\s*$/,
  ]
  for (const re of suffixPatterns) {
    s = s.replace(re, '').trim()
  }
  return s
}

/**
 * Retire un préfixe du type "X plays" / "X performs" / "X –" en début de titre.
 */
function stripPerformerPlays(t) {
  if (!t || typeof t !== 'string') return t
  const trimmed = t.trim()
  const playsMatch = trimmed.match(/^[^–—:]+?\s+(?:plays?|performs?)\s+/i)
  if (playsMatch) return trimmed.slice(playsMatch[0].length).trim()
  const dashPrefix = trimmed.match(/^[^–—:]+\s*[–—]\s+/)
  if (dashPrefix && dashPrefix[0].length < 50) return trimmed.slice(dashPrefix[0].length).trim()
  return trimmed
}

/**
 * Retire en fin de titre une parenthèse de type "(Interprète1, Interprète2)" pour éviter
 * de laisser des déchets dans workTitle et d'empêcher le match avec works.json.
 */
function stripTrailingInterpreters(t) {
  if (!t || typeof t !== 'string') return t
  const trimmed = t.trim()
  const m = trimmed.match(/\s*\(([^)]+)\)\s*$/)
  if (!m) return trimmed
  const inner = m[1].trim()
  // Ne pas retirer si ça ressemble à un numéro d'opus/catalogue (op. 31, BWV 565, etc.)
  if (/\b(op\.?|no\.?|BWV|K\.|D\.|WoO|H\.|Wq\.|L\.)\b/i.test(inner)) return trimmed
  return trimmed.slice(0, m.index).trim()
}

/**
 * Retourne le titre sans le segment final " - Chef / Orchestre" ou " - Orchestre ∙ Chef" quand présent.
 */
function stripConductorOrchestra(t) {
  if (!t || typeof t !== 'string') return t
  const trimmed = t.trim()
  const lastDash = trimmed.match(/\s+[-–—]\s+([^-–—]+)$/)
  if (!lastDash) return trimmed
  const afterLastDash = lastDash[1].trim()
  if (afterLastDash.includes('/')) return trimmed.slice(0, lastDash.index).trim()
  if (afterLastDash.includes('∙') || afterLastDash.includes('·')) return trimmed.slice(0, lastDash.index).trim()
  if (/\b(?:orchester|orchestra|philharmonic|symphony\s+orchestra|sinfonieorchester)\b/i.test(afterLastDash)) return trimmed.slice(0, lastDash.index).trim()
  return trimmed
}

/**
 * Extrait compositeur (début court) et titre d'œuvre quand le titre n'a pas de " - " (ex. "Mozart Symphony # 31 in D major").
 * Utilise les mots-clés multilingues (Sinfonie, Sonate, Konzert, etc.).
 */
function splitComposerAndWork(mainTitle) {
  if (!mainTitle || typeof mainTitle !== 'string') return { composer: null, workTitle: mainTitle || '' }
  const m = mainTitle.trim()
  const workStart = m.match(WORK_TYPE_REGEX)
  if (workStart && workStart.index > 0) {
    const composer = m.slice(0, workStart.index).trim()
    const workTitle = m.slice(workStart.index).trim()
    if (composer.length > 0 && composer.length < 50) return { composer, workTitle }
  }
  return { composer: null, workTitle: m }
}

/**
 * Segmente le titre sur les séparateurs courants (tiret, deux-points, bullet).
 * @param {string} s
 * @returns {string[]}
 */
function segmentTitle(s) {
  if (!s || typeof s !== 'string') return []
  return s.trim().split(/\s*[-–—:∙·]\s+/).map((seg) => seg.trim()).filter(Boolean)
}

/**
 * Trouve un compositeur connu dans la liste (match par slug ou nom exact).
 * @param {string} segment
 * @param {Array<{ name: string, slug: string }>} knownComposerNames
 * @returns {{ name: string, slug: string } | null}
 */
function findKnownComposerInSegment(segment, knownComposerNames) {
  if (!segment || !Array.isArray(knownComposerNames) || knownComposerNames.length === 0) return null
  const segmentSlug = composerToSlug(segment)
  if (!segmentSlug) return null
  const bySlug = knownComposerNames.find((c) => c.slug === segmentSlug)
  if (bySlug) return bySlug
  const byName = knownComposerNames.find((c) => c.name === segment || segment === c.name)
  if (byName) return byName
  return null
}

/**
 * Parse le titre YouTube pour extraire compositeur, titre de l'œuvre et éventuel mouvement.
 * @param {string} youtubeTitle - Titre brut
 * @param {Array<{ name: string, slug: string }>} [knownComposerNames] - Liste des compositeurs connus (works.json) pour priorité "compositeur d'abord"
 */
export function parseYouTubeTitle(youtubeTitle, knownComposerNames) {
  if (!youtubeTitle) {
    return { composer: null, workTitle: null, movementTitle: null }
  }

  const raw = youtubeTitle.trim()
  let mainTitle = stripConductorOrchestra(raw)
  mainTitle = stripCommonSuffixes(mainTitle)
  mainTitle = stripPerformerPlays(mainTitle)

  // Priorité "compositeur d'abord" : segmenter et chercher un segment = compositeur connu
  if (Array.isArray(knownComposerNames) && knownComposerNames.length > 0) {
    const segments = segmentTitle(mainTitle)
    for (let i = 0; i < segments.length; i++) {
      const found = findKnownComposerInSegment(segments[i], knownComposerNames)
      if (found) {
        const restSegments = segments.filter((_, j) => j !== i)
        const workCandidateRaw = (restSegments.find((seg) => WORK_TYPE_REGEX.test(seg)) || restSegments.join(' - ')).trim()
        const workCandidate = stripTrailingInterpreters(workCandidateRaw)
        const { workTitle: wt, movementTitle: mt } = extractMovementFromWorkTitle(workCandidate || workCandidateRaw)
        const out = { composer: found.name, workTitle: stripTrailingInterpreters(wt || workCandidate || mainTitle), movementTitle: mt || null }
        return out
      }
    }
    // Fallback : compositeur présent dans le titre (ex. "Beethoven sonata 17" ou "INTERPRETE plays Beethoven sonata 17" après stripPerformerPlays)
    for (const comp of knownComposerNames) {
      const name = comp.name
      if (!name || name.length > 45) continue
      const re = new RegExp('\\b' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\b', 'i')
      const idx = mainTitle.search(re)
      if (idx === -1) continue
      const before = mainTitle.slice(0, idx).trim()
      const after = mainTitle.slice(idx + name.length).trim()
      const workCandidate = (before + ' ' + after).replace(/\s+/g, ' ').trim()
      if (!workCandidate) continue
      const { workTitle: wt, movementTitle: mt } = extractMovementFromWorkTitle(workCandidate)
      return { composer: name, workTitle: stripTrailingInterpreters(wt || workCandidate), movementTitle: mt || null }
    }
  }

  // Format "Compositeur : Œuvre" (ex. "Beethoven: 8. Sinfonie ∙ hr")
  const colonMatch = mainTitle.match(/^([^:]+):\s*(.+)$/)
  if (colonMatch) {
    const left = colonMatch[1].trim()
    let right = colonMatch[2].trim()
    const suffixBullet = right.match(/\s*[∙·]\s*[a-z0-9\s]+$/i)
    if (suffixBullet) right = right.slice(0, suffixBullet.index).trim()
    if (left.length > 0 && left.length < 50 && !WORK_TYPE_REGEX.test(left)) {
      const { workTitle: finalWorkTitle, movementTitle } = extractMovementFromWorkTitle(right)
      return { composer: left, workTitle: stripTrailingInterpreters(finalWorkTitle || right), movementTitle: movementTitle || null }
    }
  }

  // Si après retrait "Chef / Orchestre" il reste un " - ", parser "Compositeur - Œuvre" ; sinon split compositeur/œuvre sur mots-clés (Symphony, etc.)
  const dashMatch = mainTitle.match(/^(.+?)\s*[-–—]\s*(.+)$/)
  if (dashMatch) {
    const left = dashMatch[1].trim()
    let right = dashMatch[2].trim()
    const cleanMatch = right.match(/^([^/·]+?)(?:\s*[\/·].*)?$/)
    if (cleanMatch) right = cleanMatch[1].trim()
    const { workTitle: finalWorkTitle, movementTitle } = extractMovementFromWorkTitle(right)
    if (!WORK_TYPE_REGEX.test(left) && left.length < 60) {
      return { composer: left, workTitle: stripTrailingInterpreters(finalWorkTitle || right), movementTitle: movementTitle || null }
    }
    const split = splitComposerAndWork(mainTitle)
    const { workTitle: wt, movementTitle: mt } = extractMovementFromWorkTitle(split.workTitle)
    return { composer: split.composer, workTitle: stripTrailingInterpreters(wt || split.workTitle), movementTitle: mt || null }
  }

  const split = splitComposerAndWork(mainTitle)
  const { workTitle: finalWorkTitle, movementTitle } = extractMovementFromWorkTitle(split.workTitle)
  return { composer: split.composer, workTitle: stripTrailingInterpreters(finalWorkTitle || split.workTitle || mainTitle), movementTitle: movementTitle || null }
}

